/**
 * Error Monitoring and Management Hooks
 * 
 * Provides comprehensive error tracking, analysis, and resolution workflows for
 * application errors. Integrates with the application_errors table to centralize
 * error reporting, deduplication, and admin resolution tracking.
 * 
 * **Error Categories:**
 * - api_error: Backend API and edge function errors
 * - ui_error: Frontend component and rendering errors
 * - auth_error: Authentication and authorization failures
 * - payment_error: Payment processing errors
 * - validation_error: Data validation failures
 * - network_error: Network connectivity issues
 * - unknown: Uncategorized errors
 * 
 * **Severity Levels:**
 * - info: Informational, no user impact
 * - low: Minor issue, degraded experience
 * - medium: Moderate issue, some functionality affected
 * - high: Significant issue, major functionality broken
 * - critical: System-breaking issue, immediate attention required
 * 
 * **Error Deduplication:**
 * Errors are deduplicated by error_id (hash of error type + message). Duplicate
 * occurrences increment occurrence_count and update last_occurrence timestamp.
 * 
 * **Error Tracking:**
 * - first_occurrence: When error first appeared
 * - last_occurrence: Most recent occurrence
 * - occurrence_count: Total times error occurred
 * - resolved: Whether error has been fixed
 * - resolution_notes: Admin notes on fix
 * 
 * **Key Features:**
 * - Filter errors by severity, type, status, date
 * - Search errors by message content
 * - View detailed error information with stack traces
 * - Track error occurrences over time
 * - Calculate error statistics and trends
 * - Resolve/reopen errors with notes
 * - Auto-refresh for real-time monitoring
 * 
 * **Use Cases:**
 * - Admin error dashboard
 * - Production error monitoring
 * - Error trend analysis
 * - Issue tracking and resolution
 * - System health monitoring
 * 
 * @module useErrorMonitoring
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface ApplicationError {
  id: string;
  error_id: string;
  error_type: string;
  error_category: string;
  severity: string;
  message: string;
  stack_trace?: string;
  component_path?: string;
  user_id?: string;
  organization_id?: string;
  url?: string;
  user_agent?: string;
  browser_info?: any;
  context?: any;
  resolved: boolean;
  resolved_at?: string;
  resolved_by?: string;
  resolution_notes?: string;
  first_occurrence: string;
  last_occurrence: string;
  occurrence_count: number;
  created_at: string;
  updated_at: string;
}

export interface ErrorOccurrence {
  id: string;
  error_id: string;
  user_id?: string;
  session_id?: string;
  context?: any;
  occurred_at: string;
}

export interface ErrorStats {
  totalErrors: number;
  unresolvedErrors: number;
  criticalErrors: number;
  errorRate24h: number;
  errorsByType: Record<string, number>;
  errorsBySeverity: Record<string, number>;
  topErrors: ApplicationError[];
}

/**
 * Hook for fetching and filtering application errors
 * 
 * Provides real-time error monitoring with comprehensive filtering capabilities.
 * Auto-refreshes every 30 seconds to keep error data current.
 * 
 * **Filtering Options:**
 * - severity: Filter by error severity level
 * - errorType: Filter by error type (api_error, ui_error, etc.)
 * - resolved: Show only resolved or unresolved errors
 * - dateFrom/dateTo: Date range filter
 * - search: Search in error messages
 * 
 * @param filters - Optional filters for error query
 * @returns {Object} React Query result with error data
 * @returns {ApplicationError[]} data - Array of filtered errors
 * @returns {boolean} isLoading - Loading state
 * @returns {Error} error - Query error if any
 * @returns {Function} refetch - Manually refresh data
 * 
 * @example
 * // Monitor unresolved critical errors
 * const { data: errors, isLoading } = useErrorMonitoring({
 *   severity: 'critical',
 *   resolved: false
 * });
 * 
 * if (isLoading) return <Spinner />;
 * 
 * return (
 *   <Alert variant="destructive">
 *     <AlertTitle>Critical Errors: {errors?.length || 0}</AlertTitle>
 *     {errors?.map(error => (
 *       <ErrorCard key={error.id} error={error} />
 *     ))}
 *   </Alert>
 * );
 * 
 * @example
 * // Search for specific error messages
 * const { data: errors } = useErrorMonitoring({
 *   search: 'payment processing failed',
 *   dateFrom: startOfWeek(new Date())
 * });
 */
export function useErrorMonitoring(filters?: {
  severity?: string;
  errorType?: string;
  resolved?: boolean;
  dateFrom?: Date;
  dateTo?: Date;
  search?: string;
}) {
  return useQuery({
    queryKey: ['application-errors', filters],
    queryFn: async () => {
      let query = supabase
        .from('application_errors')
        .select('*')
        .order('last_occurrence', { ascending: false });

      if (filters?.severity) {
        query = query.eq('severity', filters.severity);
      }

      if (filters?.errorType) {
        query = query.eq('error_type', filters.errorType);
      }

      if (filters?.resolved !== undefined) {
        query = query.eq('resolved', filters.resolved);
      }

      if (filters?.dateFrom) {
        query = query.gte('last_occurrence', filters.dateFrom.toISOString());
      }

      if (filters?.dateTo) {
        query = query.lte('last_occurrence', filters.dateTo.toISOString());
      }

      if (filters?.search) {
        query = query.ilike('message', `%${filters.search}%`);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ApplicationError[];
    },
    refetchInterval: 30000 // Refetch every 30 seconds
  });
}

/**
 * Hook for fetching aggregated error statistics
 * 
 * Calculates and returns comprehensive error metrics for dashboard display:
 * - Total error count
 * - Unresolved error count
 * - Critical unresolved errors
 * - 24-hour error rate
 * - Distribution by type and severity
 * - Top 5 most frequent errors
 * 
 * Auto-refreshes every minute to keep statistics current.
 * 
 * @returns {Object} React Query result with error statistics
 * @returns {ErrorStats} data - Aggregated error statistics
 * @returns {boolean} isLoading - Loading state
 * @returns {Function} refetch - Manually refresh stats
 * 
 * @example
 * // Display error statistics dashboard
 * const { data: stats, isLoading } = useErrorStats();
 * 
 * if (isLoading) return <Spinner />;
 * 
 * return (
 *   <Grid>
 *     <StatCard 
 *       title="Total Errors" 
 *       value={stats.totalErrors}
 *       trend={stats.errorRate24h > 10 ? 'up' : 'stable'}
 *     />
 *     <StatCard 
 *       title="Unresolved" 
 *       value={stats.unresolvedErrors}
 *       alert={stats.criticalErrors > 0}
 *     />
 *     <StatCard 
 *       title="24h Rate" 
 *       value={stats.errorRate24h}
 *     />
 *     <ErrorDistributionChart data={stats.errorsByType} />
 *   </Grid>
 * );
 */
export function useErrorStats() {
  return useQuery({
    queryKey: ['error-stats'],
    queryFn: async () => {
      const { data: errors, error } = await supabase
        .from('application_errors')
        .select('*');

      if (error) throw error;

      const now = new Date();
      const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);

      const errors24h = errors?.filter(e => new Date(e.last_occurrence) > yesterday) || [];

      const stats: ErrorStats = {
        totalErrors: errors?.length || 0,
        unresolvedErrors: errors?.filter(e => !e.resolved).length || 0,
        criticalErrors: errors?.filter(e => e.severity === 'critical' && !e.resolved).length || 0,
        errorRate24h: errors24h.length,
        errorsByType: {},
        errorsBySeverity: {},
        topErrors: errors?.sort((a, b) => b.occurrence_count - a.occurrence_count).slice(0, 5) || []
      };

      errors?.forEach(error => {
        stats.errorsByType[error.error_type] = (stats.errorsByType[error.error_type] || 0) + 1;
        stats.errorsBySeverity[error.severity] = (stats.errorsBySeverity[error.severity] || 0) + 1;
      });

      return stats;
    },
    refetchInterval: 60000 // Refetch every minute
  });
}

/**
 * Hook for fetching detailed error information
 * 
 * Loads complete error details including all occurrences for a specific error.
 * Shows error history, affected users, and occurrence patterns.
 * 
 * @param errorId - The error_id to fetch details for
 * @returns {Object} React Query result with error details
 * @returns {ApplicationError} data.error - Error record
 * @returns {ErrorOccurrence[]} data.occurrences - Error occurrence history
 * 
 * @example
 * // View error details page
 * const { data, isLoading } = useErrorDetails(errorId);
 * 
 * if (isLoading) return <Spinner />;
 * 
 * return (
 *   <div>
 *     <ErrorHeader error={data.error} />
 *     <OccurrenceTimeline occurrences={data.occurrences} />
 *   </div>
 * );
 */
export function useErrorDetails(errorId: string) {
  return useQuery({
    queryKey: ['error-details', errorId],
    queryFn: async () => {
      const [errorResponse, occurrencesResponse] = await Promise.all([
        supabase
          .from('application_errors')
          .select('*')
          .eq('error_id', errorId)
          .single(),
        supabase
          .from('error_occurrences')
          .select('*')
          .eq('error_id', errorId)
          .order('occurred_at', { ascending: false })
          .limit(100)
      ]);

      if (errorResponse.error) throw errorResponse.error;

      return {
        error: errorResponse.data as ApplicationError,
        occurrences: occurrencesResponse.data as ErrorOccurrence[]
      };
    },
    enabled: !!errorId
  });
}

/**
 * Hook for resolving errors
 * 
 * Marks errors as resolved with optional resolution notes. Used by admins
 * after fixing underlying issues. Resolved errors are excluded from active
 * monitoring but retained for historical analysis.
 * 
 * @returns {Object} Mutation object for resolving errors
 * @returns {Function} mutate - Function to resolve error
 * @returns {boolean} isPending - Mutation in progress
 * 
 * @example
 * // Resolve error after fix deployed
 * const { mutate: resolveError, isPending } = useResolveError();
 * 
 * const handleResolve = () => {
 *   resolveError({
 *     errorId: error.error_id,
 *     notes: 'Fixed by updating payment validation logic in PR #234'
 *   });
 * };
 */
export function useResolveError() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ 
      errorId, 
      notes 
    }: { 
      errorId: string; 
      notes?: string;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();

      const { error } = await supabase
        .from('application_errors')
        .update({
          resolved: true,
          resolved_at: new Date().toISOString(),
          resolved_by: user?.id,
          resolution_notes: notes,
          updated_at: new Date().toISOString()
        })
        .eq('error_id', errorId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['application-errors'] });
      queryClient.invalidateQueries({ queryKey: ['error-stats'] });
      toast({
        title: 'Error Resolved',
        description: 'The error has been marked as resolved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to Resolve Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
}

/**
 * Hook for reopening resolved errors
 * 
 * Reopens previously resolved errors when they recur or the fix was ineffective.
 * Clears resolution data and marks error as unresolved for admin attention.
 * 
 * @returns {Object} Mutation object for reopening errors
 * @returns {Function} mutate - Function to reopen error
 * @returns {boolean} isPending - Mutation in progress
 * 
 * @example
 * // Reopen error that recurred
 * const { mutate: reopenError } = useReopenError();
 * 
 * const handleReopen = () => {
 *   reopenError(error.error_id);
 *   toast({ 
 *     title: 'Error Reopened', 
 *     description: 'Marked as unresolved for further investigation' 
 *   });
 * };
 */
export function useReopenError() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (errorId: string) => {
      const { error } = await supabase
        .from('application_errors')
        .update({
          resolved: false,
          resolved_at: null,
          resolved_by: null,
          resolution_notes: null,
          updated_at: new Date().toISOString()
        })
        .eq('error_id', errorId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['application-errors'] });
      queryClient.invalidateQueries({ queryKey: ['error-stats'] });
      toast({
        title: 'Error Reopened',
        description: 'The error has been marked as unresolved.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Failed to Reopen Error',
        description: error.message,
        variant: 'destructive',
      });
    }
  });
}
